import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { allEquipmentTypesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type EquipmentTypeId, isOfferableType, type SpaceId } from '@rock/types';
import { type FC, useEffect, useMemo, useState } from 'react';
import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';

/**
 * Pick ONE catalog type — the single-select sibling of `EquipmentTypeMultiSelect`, wrapped in its own
 * dialog because the acts it serves (retiring a custom onto a type, pointing a candidate at one) are
 * deliberate, not field edits. The search box opens pre-filled with the best free-text hint we hold
 * (the custom's title or the enrichment's `details.type`), so the near-exact match — "Tankless Water
 * Heater" → `tankless-water-heater` — is usually the first row; `initialSelection` skips the search
 * entirely when the caller already knows the likely answer.
 */
export const RemapTypeDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    /** Ranks the picker (own space first) — never filters it. */
    spaceId: SpaceId;
    /** Pre-fills the search box; the admin edits freely. */
    initialQuery?: string;
    /** Opens with this type already chosen (still removable) — skips the search for the obvious pick. */
    initialSelection?: EquipmentTypeId;
    /** Verb for the confirm button and the chosen-type line, for callers that aren't remapping. */
    confirmLabel?: string;
    busyLabel?: string;
    selectionLabel?: string;
    busy?: boolean;
    onConfirm: (targetTypeId: EquipmentTypeId) => void;
}> = ({
    open,
    onOpenChange,
    title,
    description,
    spaceId,
    initialQuery,
    initialSelection,
    confirmLabel = 'Remap',
    busyLabel = 'Remapping…',
    selectionLabel = 'Remap to',
    busy,
    onConfirm,
}) => {
    const equipmentTypes = useAsyncAtomValue(allEquipmentTypesState, []);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<EquipmentTypeId | undefined>();

    // Re-seed the search each time the dialog opens (it may open for a different custom).
    useEffect(() => {
        if (open) {
            setQuery(initialQuery ?? '');
            setSelected(initialSelection);
        }
    }, [open, initialQuery, initialSelection]);

    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return equipmentTypes
            .filter(isOfferableType)
            .filter((type) => !needle || type.id.includes(needle) || type.title.toLowerCase().includes(needle))
            .sort((a, b) => {
                const home = Number(b.spaceId === spaceId) - Number(a.spaceId === spaceId);
                return home || a.title.localeCompare(b.title);
            })
            .slice(0, 8);
    }, [equipmentTypes, query, spaceId]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-3">
                    {selected ? (
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{selectionLabel}</span>
                            <EquipmentTypeBadge typeId={selected} onRemove={() => setSelected(undefined)} />
                        </div>
                    ) : (
                        <>
                            <Input
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Search catalog types…"
                                autoComplete="off"
                                autoFocus
                            />
                            <div className="max-h-56 overflow-auto rounded-md border">
                                {matches.map((type) => (
                                    <button
                                        key={type.id}
                                        type="button"
                                        onClick={() => setSelected(type.id)}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                                    >
                                        <span className="truncate">{type.title}</span>
                                        {type.spaceId !== spaceId && (
                                            <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground">
                                                {type.spaceId}
                                            </span>
                                        )}
                                        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                            {type.id}
                                        </span>
                                    </button>
                                ))}
                                {matches.length === 0 && (
                                    <div className="px-3 py-2 section-description">No matching types</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy} type="button">
                        Cancel
                    </Button>
                    <Button type="button" disabled={!selected || busy} onClick={() => selected && onConfirm(selected)}>
                        {busy ? busyLabel : confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
